import React, {
  FunctionComponent, useEffect, useState,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  RegisteredNodePrivileges,
} from 'data/registeredNode/registeredNode.types';
import * as actions from 'data/registeredNode/registeredNode.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/registeredNode/registeredNode.selectors';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AppToaster } from 'shared/toaster';
import DistributorGrid from './components/RegisteredNodeGrid';
import { registeredNodeColumnDefs } from '../../RegisteredNode.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getRegisteredNodes(state),
  selectedRegisteredNode: selectors.getSelectedRegisteredNode(state),
  userDBId: state.authentication.userDBId,
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
});

const mapDispatch = {
  goToRegisteredNodeCreate: actions.goToRegisteredNodeCreate,
  goToRegisteredNodeEdit: actions.goToRegisteredNodeEdit,
  goToRegisteredNodeView: actions.goToRegisteredNodeView,
  selectRegisteredNode: actions.selectRegisteredNode,
  getRegisteredNodeList: actions.getRegisteredNodes,
  deleteRegisteredNode: actions.deleteRegisteredNode,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const RegisteredNodeList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data,
  selectedRegisteredNode,
  goToRegisteredNodeCreate,
  goToRegisteredNodeEdit,
  goToRegisteredNodeView,
  selectRegisteredNode,
  getRegisteredNodeList,
  deleteRegisteredNode,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColDef[]>(registeredNodeColumnDefs);

  useEffect(() => {
    getRegisteredNodeList(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [deliveryFunctionId, getRegisteredNodeList]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getRegisteredNodeList(deliveryFunctionId);

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToRegisteredNodeCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectRegisteredNode(event.api.getSelectedRows());
  };

  const handleDeleteRegisteredNode = () => {
    const distributorConfigrationId = selectedRegisteredNode[0]?.id;
    if (distributorConfigrationId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedRegisteredNode[0]?.id,
        AuditService.REGISTERED_NODES,
        AuditActionType.REGISTERED_NODES_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteRegisteredNode(deliveryFunctionId, distributorConfigrationId, body);
  };

  const handleGoToEdit = () => goToRegisteredNodeEdit(history);

  const handleGoToView = () => goToRegisteredNodeView(history);

  const hasPrivilegeToView = privileges.includes(RegisteredNodePrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(RegisteredNodePrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(RegisteredNodePrivileges.Delete);

  return (
    <div data-testid="RegisteredNodeList" className="GridPageContainer">
      <DistributorGrid
        data={data}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        lastUpdatedAt={lastUpdatedAt}
        selectedRegisteredNode={selectedRegisteredNode[0]}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handleDeleteRegisteredNode}
        onEdit={handleGoToEdit}
        onView={handleGoToView}
      />
    </div>
  );
};

export default withRouter(connector(RegisteredNodeList));
